import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { http, mediaUrl } from "@/lib/api";
import { Logo } from "@/components/shared";

const POLL_MS = 5 * 60 * 1000;   // refresh rotation while online
const FLUSH_MS = 45 * 1000;       // batch heartbeats at most every 45s
const BACKOFF_MS = [15 * 1000, 60 * 1000, 5 * 60 * 1000]; // reconnect backoff

// Play media via in-memory blob URLs instead of hitting the network / SW at
// playback time. Cache-served <video> breaks on older webviews (Chrome 95 /
// Android 9) on the second media load — the SW can't service Range requests,
// the element errors and we'd be stuck on a title card. Blob URLs are
// complete, in-memory, and free of Range/streaming issues. Each slot gets its
// own fresh object URL (even when two slots reuse the same file) so no video
// element ever replays a consumed source. The SW media cache is still filled
// in the same pass so offline keeps working via the cache.
const MEDIA_CACHE = "anand-media";
function mediaSrc(it) { return it?.video ? mediaUrl(it.video) : null; }
async function prepareMedia(items) {
  const fresh = {};
  let allOk = true;
  const entries = items.map((it, i) => ({ i, src: mediaSrc(it) }));
  if (entries.length === 0) return { fresh, allOk };
  // Never let the SW cache block/keep playback hostage: blobs are the primary
  // source, the cache is filled in the background afterwards. A wall-clock
  // deadline means worst case we fall back to network playback (below) instead
  // of a blank screen.
  const deadline = Date.now() + 25000;
  const puts = [];
  // Parallel per-slot fetching: a slow/waning hotspot must never let early
  // slots starve later ones out of the whole blob window (that left slots 2-4
  // blob-less exactly like the cab's symptom).
  const results = await Promise.all(entries.map(async ({ i, src }) => {
    if (!src) return null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      if (Date.now() > deadline) break;
      try {
        const res = await fetch(src, { cache: "force-cache" });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const copy = res.clone();
        const blob = await res.blob();
        if (!blob || blob.size === 0) throw new Error("empty blob");
        puts.push({ src, copy });
        return { i, url: URL.createObjectURL(blob) };
      } catch {
        if (attempt < 3 && Date.now() <= deadline) {
          await new Promise((r) => setTimeout(r, 2000 * attempt));
        }
      }
    }
    return null;
  }));
  for (const r of results) {
    if (r) fresh[r.i] = r.url;
    else allOk = false;
  }
  if (puts.length && typeof caches !== "undefined") {
    caches.open(MEDIA_CACHE).then((cache) =>
      Promise.all(puts.map(({ src, copy }) => cache.put(src, copy).catch(() => {})))
    ).catch(() => {});
  }
  return { fresh, allOk };
}

// When a blob isn't ready for a slot, play a cache-busted network URL so the
// <video> request misses the SW's CacheStorage (whose responses break old
// Chrome's Range handling) and streams from the server like the first ad did.
function networkSrc(it) {
  const m = mediaSrc(it);
  return m ? `${m}?b=${Date.now()}` : null;
}

// ONE persistent <video> element for the whole rotation. Chrome 95 on the cab
// SoC (T68) fails to REINITIALIZE its decoder when slots mount fresh elements
// back-to-back ("video decoder reinitialization failed" — PIPELINE_ERROR_DECODE).
// So on each slot change we release the previous source (pause; src=""; load()),
// wait a short handover gap for the decoder to fully tear down, then assign the
// next blob/network source. preload stays off so only the current ad decodes.
// If the decoder errors twice in a row, degrade the session to title cards so
// the rotation keeps moving instead of crash-looping.
const FAIL_CARD_MS = 5000;
const HANDOVER_MS = 400;

export default function CabPlayer() {
  const { plate } = useParams();
  const debug = new URLSearchParams(window.location.search).get("debug") === "1";
  const [rot, setRot] = useState(null);
  const [idx, setIdx] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const [offlineCached, setOfflineCached] = useState(false);
  const queueRef = useRef([]);
  const lastFlush = useRef(0);
  const doneRef = useRef(null);
  const blobMap = useRef({});
  const [, setBlobTick] = useState(0);
  const videoRef = useRef(null);
  const idxRef = useRef(0);
  const rotRef = useRef(null);
  const handoverTimer = useRef(null);
  const errCountRef = useRef(0);
  const [videoDisabled, setVideoDisabled] = useState(false);
  const [failedSlot, setFailedSlot] = useState(false);
  const [lastErr, setLastErr] = useState(null);

  // Prepare every rotation slot's playback: fetch each video once (browser
  // cache-first), turn it into a fresh per-slot in-memory blob URL, and keep
  // the SW media cache filled for offline. Called whenever the rotation
  // (re)loads, including the cache-restore path. Returns true only when every
  // video could be prepared (caller defers the rotation-ack until then so the
  // next poll re-tries).
  const prepare = useCallback(async (items) => {
    if (!items) return true;
    const { fresh, allOk } = await prepareMedia(items);
    for (const k of Object.keys(blobMap.current)) {
      if (!fresh[k]) URL.revokeObjectURL(blobMap.current[k]);
    }
    blobMap.current = fresh;
    setBlobTick((n) => n + 1);
    return allOk;
  }, []);

  const advance = useCallback(() => {
    setIdx((i) => (rot?.items?.length ? (i + 1) % rot.items.length : 0));
  }, [rot]);

  // Installable fullscreen home-screen app: swap in a per-cab web manifest so
  // "Add to Home screen" on the tablet launches this plate immersive (no URL
  // bar / status bar), and re-names the icon for the right cab.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]')
      || (() => { const l = document.createElement("link"); l.rel = "manifest"; document.head.appendChild(l); return l; })();
    const manifest = {
      name: `Anand · ${plate}`,
      short_name: "Anand",
      start_url: `/cab/${plate}`,
      display: "fullscreen",
      orientation: "landscape",
      background_color: "#000000",
      theme_color: "#000000",
      icons: [
        { src: "/anand-logo.png", sizes: "192x192", type: "image/png" },
        { src: "/anand-logo-white.png", sizes: "512x512", type: "image/png" },
      ],
    };
    const url = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" }));
    link.href = url;
    return () => { URL.revokeObjectURL(url); link.href = ""; };
  }, [plate]);

  // manual timing fallback (placeholder ads have no <video> to end)
  useEffect(() => {
    if (!rot?.items?.length) return;
    const dur = (rot.items[idx]?.duration || 15) * 1000;
    const t = setTimeout(advance, dur);
    return () => clearTimeout(t);
  }, [idx, rot, advance]);

  const trackPlay = useCallback((item) => {
    queueRef.current.push({ creative_id: item.creative_id, slot: item.slot, at: new Date().toISOString() });
    const now = Date.now();
    if (queueRef.current.length >= 8 || now - lastFlush.current >= FLUSH_MS) {
      const plays = queueRef.current.splice(0);
      lastFlush.current = now;
      if (navigator.onLine) {
        http.post(`/cab-screens/${rot?.plate || plate}/heartbeat`, { plays, app_version: window.anandAppVersion }).catch(() => { queueRef.current.unshift(...plays); });
      }
    }
  }, [plate, rot?.plate]);
  doneRef.current = (item) => { trackPlay(item); advance(); };

  // Decoder events on the single persistent element. A second consecutive
  // decode error (T68 "reinitialization failed") degrades the session to title
  // cards so the rotation keeps moving.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onEnded = () => {
      const it = rotRef.current?.items?.[idxRef.current];
      if (it) doneRef.current(it);
    };
    const onError = () => {
      const vv = videoRef.current;
      setLastErr((vv?.error && (vv.error.message || `code ${vv.error.code}`)) || "decode error");
      errCountRef.current += 1;
      if (errCountRef.current >= 2) setVideoDisabled(true);
      setFailedSlot(true);
      const it = rotRef.current?.items?.[idxRef.current];
      if (it) setTimeout(() => doneRef.current(it), FAIL_CARD_MS);
    };
    v.addEventListener("ended", onEnded);
    v.addEventListener("error", onError);
    return () => { v.removeEventListener("ended", onEnded); v.removeEventListener("error", onError); };
  }, []);

  // Slot change → release the previous decode session, wait the handover gap,
  // then assign the current slot's source. No fresh element per ad anymore.
  useEffect(() => {
    const v = videoRef.current;
    clearTimeout(handoverTimer.current);
    handoverTimer.current = null;
    if (!v) return;
    v.pause();
    v.removeAttribute("src");
    v.load();
    setFailedSlot(false);
    setLastErr(null);
    const it = rotRef.current?.items?.[idxRef.current];
    if (!it?.video) return;
    if (videoDisabled) { setFailedSlot(true); return; }
    handoverTimer.current = setTimeout(() => {
      const src = blobMap.current[idxRef.current] || networkSrc(it);
      v.src = src;
      v.load();
      v.play().catch(() => { setFailedSlot(true); });
    }, HANDOVER_MS);
    return () => { clearTimeout(handoverTimer.current); };
  }, [idx, rot?.items, videoDisabled]);

  const load = useCallback(async () => {
    try {
      // Change-aware: first ask if anything new is waiting (tiny response).
      // Only pull the full rotation + videos when a change exists — this is
      // what keeps a cab's data use near-zero on a hotspot unless an ad/app
      // actually changed.
      const n = await http.get(`/cab-screens/${plate}/sync-needed`);
      const serverFp = n.data.content_fingerprint;
      const cachedRaw = localStorage.getItem(`anand-rot-${plate}`);
      const cachedFp = cachedRaw ? (JSON.parse(cachedRaw).content_fingerprint || null) : null;
      // Self-heal against the server's "changed" signal being consumed by a
      // preview/ops read before THIS device cached it: if our cached rotation's
      // fingerprint no longer matches what the server would serve, force a
      // re-download even when needs_sync is false.
      const needsSync = n.data.needs_sync === true ||
        (cachedRaw !== null && cachedFp !== serverFp);
      setOnline(true);
      if (!needsSync && cachedRaw) {
        setOfflineCached(false);
        const cached = JSON.parse(cachedRaw);
        setRot(cached);
        prepare(cached.items);
        return; // nothing changed — keep playing what we have, no download
      }
      const r = await http.get(`/cab-screens/${plate}/rotation`);
      setRot(r.data);
      setVideoDisabled(false);
      errCountRef.current = 0;
      setFailedSlot(false);
      localStorage.setItem(`anand-rot-${plate}`, JSON.stringify(r.data));
      setOfflineCached(false);
      // Prepare per-slot blobs + SW cache BEFORE confirming: only ack once every
      // video is safely stored, so an interrupted sync leaves the fingerprint
      // mismatched and the next poll re-downloads + retries.
      const warmed = await prepare(r.data.items);
      if (warmed) {
        http.post(`/cab-screens/${plate}/rotation-ack`,
          { content_fingerprint: r.data.content_fingerprint }).catch(() => {});
      }
    } catch (e) {
      setOnline(false);
      const cached = localStorage.getItem(`anand-rot-${plate}`);
      if (cached) { setRot(JSON.parse(cached)); setOfflineCached(true); }
    }
  }, [plate]);

  useEffect(() => {
    setRot(null); setIdx(0); load();
    // Steady low-traffic poll while online (5 min). Controls ad change latency
    // when a cab stays connected, independent of reconnect events.
    const t = setInterval(() => { if (navigator.onLine) load(); setOnline(navigator.onLine); }, POLL_MS);
    // Reconnect fast-backoff: the moment hotspot comes up, check immediately,
    // then 15s / 1min, then settle back to the 5-min poll.
    let reconnectTimer = null;
    let step = 0;
    const scheduleBackoff = () => {
      clearTimeout(reconnectTimer);
      if (step >= BACKOFF_MS.length) return; // settle into the steady 5-min interval
      const delay = BACKOFF_MS[step];
      reconnectTimer = setTimeout(() => {
        if (navigator.onLine) load();
        step += 1;
        scheduleBackoff();
      }, delay);
    };
    const on = () => {
      setOnline(true);
      step = 0;
      load();          // check for changes the moment we have a network
      scheduleBackoff();
    };
    const off = () => {
      setOnline(false);
      clearTimeout(reconnectTimer);
    };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { clearInterval(t); clearTimeout(reconnectTimer); window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [load, plate]);

  const items = rot?.items || [];
  idxRef.current = idx;
  rotRef.current = rot;

  return (
    <div className="flex h-screen w-screen flex-col bg-black">
      <div className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          autoPlay muted loop={false} playsInline preload="none"
          className="absolute inset-0 h-full w-full object-contain bg-black"
        />
        {items.length === 0 ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <Logo size={72} variant="white" />
            <div className="mt-4 font-display text-2xl font-bold text-white">{rot?.advertiser_name || "Anand"}</div>
            <div className="mt-1 text-sm text-zinc-500">{online ? "No ads assigned to this screen yet" : "Ads will sync when signal returns"}</div>
            {!online && offlineCached && <div className="mt-1 text-xs text-zinc-600">Showing last synced schedule</div>}
          </div>
        ) : (
          <>{(!items[idx]?.video || failedSlot || videoDisabled) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-950 text-center">
              <div className="font-display text-3xl font-bold text-white">{items[idx].title}</div>
              <div className="mt-2 text-sm font-medium text-zinc-500">{items[idx].advertiser_name}</div>
              {debug && lastErr && <div className="mt-4 text-xs text-red-400/90">{lastErr}</div>}
            </div>
          )}</>
        )}
        <div className="absolute left-4 top-4 z-10"><Logo size={80} variant="white" /></div>
        <div className="absolute bottom-4 left-4 z-10">
          <div className="rounded-md bg-black/45 px-3 py-1.5 backdrop-blur-sm">
            <span className="font-display text-3xl font-bold tracking-wider text-white drop-shadow-lg">{rot?.plate || plate}</span>
          </div>
        </div>
        <div className="absolute bottom-4 right-4 z-10 text-right">
          <div className="rounded-md bg-black/45 px-3 py-1.5 backdrop-blur-sm">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-white/80">Advertise on Anand</div>
            <div className="font-display text-xl font-bold text-white">9420194437</div>
          </div>
        </div>
        <div className="absolute left-0 top-0 h-full w-px bg-white/10" />
        <div className="absolute right-0 top-0 h-full w-px bg-white/10" />
        <div className="absolute bottom-24 left-1/2 flex -translate-x-1/2 items-center gap-1">
          {(items || []).map((it, i) => (
            <span key={i} className="flex h-[3px] w-8 overflow-hidden rounded-full bg-white/20">
              <span key={idx === i ? "on" : "off"} className={idx === i ? "h-full w-full bg-white progress-sm" : "hidden"} />
            </span>
          ))}
        </div>
      </div>
      <div className="flex h-10 items-center justify-end px-4 text-[11px] font-medium text-zinc-600">
        {!online && <span className="mr-auto flex items-center gap-1.5 text-zinc-500"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Offline — playing cached ads</span>}
        {online && !offlineCached && <span className="mr-auto flex items-center gap-1.5"><span className="live-dot h-1.5 w-1.5 rounded-full bg-emerald-500" /> Online</span>}
        <span className="tabular-nums">S{idx + 1}/{items.length}</span>
        {debug && (
          <span className="ml-auto pl-3 font-mono text-[10px] text-zinc-500">
            src={blobMap.current[idx] ? "blob" : "net"} blobs={Object.keys(blobMap.current).length}/{items.filter((it) => it.video).length} ua=95? ver={window.anandAppVersion || "web"}
          </span>
        )}
      </div>
      <style>{`@keyframes anandProgress { from { width: 0 } to { width: 100% } } .progress-sm { animation: anandProgress ${(items[idx]?.duration || 15)}s linear forwards; }`}</style>
    </div>
  );
}